import { Editor, Node, Path } from 'slate';
import { useSlate } from 'slate-react';
import { i18n, i18nTemplate } from 'app/utils/i18n';
import { Typography } from 'app/components/ui';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { Coverage, CoverageRule, getHeadingLevel, hasCoverage, isReferenceTable } from 'app/slate/extensions/coverage/types';
import { setCoverage } from 'app/slate/extensions/coverage/transforms/setCoverage';
import { clearCoverage } from 'app/slate/extensions/coverage/transforms/clearCoverage';

import './CoverageMarkerControls.scss';

type Target =
  | { kind: 'heading'; path: Path; text: string; coverage?: Coverage; level: number }
  | { kind: 'table'; path: Path; text: string; coverage?: Coverage }
  | null;

const LABEL_MAX_CHARS = 15;
const truncateLabel = (text: string): string => (text.length > LABEL_MAX_CHARS ? `${text.slice(0, LABEL_MAX_CHARS).trimEnd()}…` : text);

const findTarget = (editor: Editor, references: Array<{ superId: string; name?: string }>): Target => {
  if (!editor.selection) return null;

  let currentPath: Path = Editor.path(editor, editor.selection);
  while (currentPath.length > 0) {
    try {
      const [node] = Editor.node(editor, currentPath);
      const level = getHeadingLevel(node);
      if (level !== null) {
        return {
          kind: 'heading',
          path: currentPath,
          text: Node.string(node),
          coverage: hasCoverage(node) ? (node as unknown as { coverage: Coverage }).coverage : undefined,
          level,
        };
      }
      if (isReferenceTable(node)) {
        const referenceDataSuperId = (node as { referenceDataSuperId?: string }).referenceDataSuperId;
        const ref = referenceDataSuperId ? references.find((r) => r.superId === referenceDataSuperId) : undefined;
        return {
          kind: 'table',
          path: currentPath,
          text: ref?.name ?? referenceDataSuperId ?? i18n('tag.coverage.table.fallbackName'),
          coverage: hasCoverage(node) ? (node as unknown as { coverage: Coverage }).coverage : undefined,
        };
      }
    } catch {
      return null;
    }
    currentPath = Path.parent(currentPath);
  }
  return null;
};

export const CoverageMarkerControls = () => {
  const editor = useSlate();
  const references = useEditorSelector((e: any) => e?.references) ?? [];
  const target = findTarget(editor, references);

  if (!target) {
    return (
      <div className="coverage-marker-controls coverage-marker-controls--empty">
        <Typography variant="body-5">{i18n('tag.coverage.controls.emptyHint')}</Typography>
      </div>
    );
  }

  const apply = (rule: CoverageRule) => setCoverage(editor, target.path, rule);
  const clear = () => clearCoverage(editor, target.path);

  const currentKind = target.coverage?.rule.kind;
  const isActive = (kind: CoverageRule['kind']) => currentKind === kind;

  return (
    <div className="coverage-marker-controls">
      <div className="coverage-marker-controls__label">
        <Typography variant="body-5" fontWeight="bold">
          {target.kind === 'heading'
            ? i18nTemplate('tag.coverage.controls.heading.label', { text: truncateLabel(target.text || i18n('tag.coverage.heading.fallback')) })
            : i18nTemplate('tag.coverage.controls.table.label', { text: truncateLabel(target.text) })}
        </Typography>
      </div>
      <div className="coverage-marker-controls__buttons">
        {target.kind === 'heading' && (
          <>
            <button
              type="button"
              className={`coverage-marker-controls__button${isActive('block') ? ' is-active' : ''}`}
              onClick={() => apply({ kind: 'block' })}
            >
              {i18n('tag.coverage.rule.block.label')}
            </button>
            <button
              type="button"
              className={`coverage-marker-controls__button${isActive('strict') ? ' is-active' : ''}`}
              onClick={() => apply({ kind: 'strict' })}
            >
              {i18n('tag.coverage.rule.strict.label')}
            </button>
          </>
        )}
        {target.kind === 'table' && (
          <button
            type="button"
            className={`coverage-marker-controls__button${isActive('all-cells') ? ' is-active' : ''}`}
            onClick={() => apply({ kind: 'all-cells' })}
          >
            {i18n('tag.coverage.rule.allCells.label')}
          </button>
        )}
        {target.coverage && (
          <button type="button" className="coverage-marker-controls__button coverage-marker-controls__button--clear" onClick={clear}>
            {i18n('tag.coverage.controls.clear.label')}
          </button>
        )}
      </div>
    </div>
  );
};
